import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';

import { focusRing } from 'ui/a11y';

import { AdminNavGroup } from './nav-items';
import { ChevronIcon } from './icons';
import { useDismissable } from './use-dismissable';

const Wrap = styled.div`
  display: flex;
`;

const Trigger = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 12px 0;
  background: transparent;
  border: 0;
  border-bottom: 3px solid ${p => (p.$active ? p.theme.colors.brown : 'transparent')};
  font-family: ${p => p.theme.font.family};
  font-size: 16px;
  font-weight: ${p => p.theme.font.weight.bold};
  white-space: nowrap;
  color: ${p => p.theme.colors.brown};
  cursor: pointer;

  &:hover,
  &[aria-expanded='true'] {
    background: ${p => p.theme.colors.yellowPale};
  }

  svg {
    transition: transform 0.15s ease;
  }

  &[aria-expanded='true'] svg {
    transform: rotate(180deg);
  }

  ${focusRing}
`;

/** 1/2/3-column panel widths — the org-view-only user's single-group panel isn't 780px wide. */
const PANEL_WIDTH: Record<number, number> = { 1: 280, 2: 520, 3: 780 };

const Panel = styled.div<{ $columns: number }>`
  position: absolute;
  z-index: 10;
  /*
   * Anchored to the bar (index.tsx's Bar, position: relative), not to this trigger's own
   * wrapper — anchoring to the trigger let the 780px-at-3-columns panel run off both edges of the
   * viewport at 900-1199px (and beyond it at some locales' widths even at 1200+, since
   * Administreerimine's own x position shifts with the trigger's preceding sibling widths, which
   * differ by locale). A left offset off the bar's own left edge plus a viewport-wide max-width
   * clamp can't overflow regardless of locale or trigger position.
   */
  top: calc(100% + 4px);
  left: 24px;
  max-width: calc(100vw - 48px);
  width: ${p => PANEL_WIDTH[p.$columns] ?? PANEL_WIDTH[3]}px;
  box-sizing: border-box;
  background: ${p => p.theme.colors.surface};
  border: 1px solid ${p => p.theme.colors.borderSubtle};
  border-radius: 8px; /* artboard-literal — not tokens.radiusLg (6px) */
  /* artboard-literal shadow: not one of tokens.shadow's presets */
  box-shadow: 0 12px 32px rgba(51, 42, 35, 0.18);
  padding: ${p => p.theme.space[2]};
  display: grid;
  grid-template-columns: repeat(${p => p.$columns}, minmax(0, 1fr));
  gap: ${p => p.theme.space[2]};
  text-align: left;
`;

const Group = styled.section`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const GroupHeading = styled.h3`
  margin: 0;
  padding: 0 12px 8px;
  font-family: ${p => p.theme.font.family};
  font-size: 14px;
  font-weight: ${p => p.theme.font.weight.bold};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${p => p.theme.colors.muted};
`;

const Item = styled(NavLink)`
  display: flex;
  align-items: flex-start;
  gap: ${p => p.theme.space.onePointFive};
  padding: 10px 12px;
  border-radius: 8px; /* artboard-literal — not tokens.radiusLg (6px) */
  color: ${p => p.theme.colors.brown};
  text-decoration: none;

  &:hover,
  &[aria-current='page'] {
    background: ${p => p.theme.colors.yellowPale};
  }

  &:hover span[data-tile],
  &[aria-current='page'] span[data-tile] {
    background: ${p => p.theme.colors.primary};
  }

  ${focusRing}
`;

const Tile = styled.span`
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px; /* artboard-literal — not tokens.radiusLg (6px) */
  background: ${p => p.theme.colors.yellowPale};
  color: ${p => p.theme.colors.brown};
`;

const ItemText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ItemTitle = styled.span`
  font-family: ${p => p.theme.font.family};
  font-size: 16px;
  font-weight: ${p => p.theme.font.weight.bold};
`;

const ItemDescription = styled.span`
  font-family: ${p => p.theme.font.family};
  font-size: 14px;
  line-height: 1.35;
  color: ${p => p.theme.colors.muted};
`;

export interface AdminPanelProps {
  groups: AdminNavGroup[];
  active: boolean;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
}

/**
 * The Administreerimine nav item: a disclosure (`V2-Haldus-A`) over plain links, grouped into up
 * to 3 columns (the column count follows `groups.length`, so an org-view-only user's single-group
 * panel isn't stretched to the full 3-column width). Not `role="menu"` — arrow keys don't move
 * focus, Tab does, like any other set of links behind a toggle.
 */
export function AdminPanel({ groups, active, open, onToggle, onClose }: AdminPanelProps) {
  const { t } = useTranslation();
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useDismissable(open, wrapRef, onClose, triggerRef);

  return (
    <Wrap ref={wrapRef}>
      <Trigger
        ref={triggerRef}
        type="button"
        $active={active}
        aria-expanded={open}
        aria-controls={open ? 'lis-admin-panel' : undefined}
        onClick={onToggle}
      >
        <span>{t('header.adminMenu')}</span>
        <ChevronIcon size={16} />
      </Trigger>
      {open && (
        <Panel id="lis-admin-panel" data-testid="lis-admin-panel" $columns={groups.length || 1}>
          {groups.map(group => (
            <Group key={group.id}>
              <GroupHeading>{t(group.headingKey)}</GroupHeading>
              {group.items.map(item => (
                <Item key={item.id} to={item.to} onClick={onClose}>
                  <Tile data-tile aria-hidden="true">
                    <item.Icon size={22} />
                  </Tile>
                  <ItemText>
                    <ItemTitle>{t(item.labelKey)}</ItemTitle>
                    <ItemDescription>{t(item.descriptionKey)}</ItemDescription>
                  </ItemText>
                </Item>
              ))}
            </Group>
          ))}
        </Panel>
      )}
    </Wrap>
  );
}

export default AdminPanel;
